import CardWithTitle from 'components/CardWithTitle';
import MarkEventAsCompleteButton from 'components/Event/MarkEventAsCompleteButton';
import NoMessage from 'components/NoMessage';
import MarkTaskAsCompleteButton from 'components/Task/MarkTaskAsCompleteButton';
import ActivityType from 'core/enums/activityType';
import EventMood from 'core/enums/eventMood';
import { ModalType } from 'core/useCases/modal/types';
import CheckboxComplete from 'images/CheckboxComplete.svg?react';
import Hourglass from 'images/Hourglass.svg?react';
import Edit from 'images/icons/Edit.svg?react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useModalStore } from 'stores/modal';
import { SMultilineText, SSubCard } from 'styles/components';
import ActivityReportVisualisation from '../ActivityReportVisualisation';
import { SButtonContainer, SButtonWrapper, SWrapper } from './styles';

type ActivityCommentAndReportProps = {
    id: string;
    activityType: ActivityType;
    comments?: string;
    markAsCompleteComment?: string;
    isAlreadyCompleted: boolean;
    mood?: EventMood;
    resources?: string;
};

const ActivityCommentAndReport: FC<ActivityCommentAndReportProps> = ({
    id,
    activityType,
    comments,
    markAsCompleteComment,
    isAlreadyCompleted,
    mood,
    resources,
}) => {
    const { t } = useTranslation();
    const openModal = useModalStore((s) => s.openModal);

    const openAdditionalInformationModal = () => {
        openModal({
            defaultValues: { comments, id, resources },
            title: t(`activity.additional-information.${comments ? 'update' : 'add'}.label`),
            type: ActivityType.Task === activityType ? ModalType.TaskAdditionalInfo : ModalType.EventAdditionalInfo,
        });
    };

    const openReportModal = () => {
        openModal({
            defaultValues: {
                activityType,
                comment: markAsCompleteComment,
                id,
                isAlreadyCompleted,
                mood,
            },
            title: t(`activity.report.${markAsCompleteComment ? 'update' : 'add'}.label`),
            type: ModalType.ActivityReport,
        });
    };

    return (
        <SWrapper>
            {comments ? (
                <CardWithTitle
                    actions={
                        <SButtonContainer>
                            <button
                                onClick={() => openAdditionalInformationModal()}
                                title={t('activity.additional-information.update.label')}
                                type="button"
                            >
                                <Edit />
                            </button>
                        </SButtonContainer>
                    }
                    label={t('common.additional-information')}
                >
                    <SSubCard>
                        <SMultilineText>{comments}</SMultilineText>
                    </SSubCard>
                </CardWithTitle>
            ) : (
                <NoMessage
                    icon={<Hourglass />}
                    label={t('common.add-information')}
                    onClick={openAdditionalInformationModal}
                    title={t('activity.additional-information.empty')}
                />
            )}
            {isAlreadyCompleted ? (
                <div>
                    {markAsCompleteComment || mood ? (
                        <CardWithTitle
                            actions={
                                <button
                                    onClick={() => openReportModal()}
                                    title={t('activity.report.update.label')}
                                    type="button"
                                >
                                    <Edit />
                                </button>
                            }
                            label={t('common.how-the-process-was')}
                        >
                            <ActivityReportVisualisation comment={markAsCompleteComment} mood={mood} />
                        </CardWithTitle>
                    ) : (
                        <NoMessage
                            icon={<CheckboxComplete />}
                            label={t('common.add-report')}
                            onClick={openReportModal}
                            title={t('activity.report.empty')}
                        />
                    )}
                </div>
            ) : (
                <SButtonWrapper>
                    {ActivityType.Task === activityType && <MarkTaskAsCompleteButton iconButton={false} taskId={id} />}
                    {ActivityType.Event === activityType && (
                        <MarkEventAsCompleteButton eventId={id} iconButton={false} />
                    )}
                </SButtonWrapper>
            )}
        </SWrapper>
    );
};

export default ActivityCommentAndReport;
